/**
 * Compact trigger + right-side slide-in panel for the conversation-list
 * CRM enrichment modules. Drives `preferences.inboxRecordEnrichmentModules`.
 *
 * - The button shows only the currently-active modules (user pins, or
 *   system defaults if not pinned yet). Click → opens a right-side panel
 *   with the full chip picker.
 * - Monday: any number of board picks (single `search.items` call).
 *   Every other adapter: capped at `ENRICHMENT_MAX_MODULES`.
 * - Changes persist immediately and wipe the enrichment IDB cache so
 *   the next pass uses the fresh module list.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw, Search, Settings2, X } from 'lucide-react';
import { useSettingsStore } from '@/stores/settings-store';
import { useModuleStore } from '@/stores/module-store';
import {
  resolveDefaultEnrichmentModules,
  getEnrichmentModulesCap,
  useConversationStore,
} from '@/stores/conversation-store';
import { idbCache } from '@/lib/cache/idb-cache';
import { CONV_ENRICHMENT_KEY_PREFIX } from '@/lib/cache/cache-keys';
import { cn } from '@/lib/utils';

export interface EnrichmentModulesPickerProps {
  className?: string;
}

export function EnrichmentModulesPicker({ className }: EnrichmentModulesPickerProps) {
  const saasModule = useModuleStore((s) => s.saasModule);
  const preferences = useSettingsStore((s) => s.preferences);
  const updatePreferences = useSettingsStore((s) => s.updatePreferences);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [resyncing, setResyncing] = useState(false);
  const resyncConversationEnrichment = useConversationStore((s) => s.resyncConversationEnrichment);

  const available = useMemo(
    () => saasModule?.getSupportedModules?.() ?? [],
    [saasModule],
  );
  const userPinned = preferences.inboxRecordEnrichmentModules ?? [];
  const systemDefaults = useMemo(
    () => (saasModule ? resolveDefaultEnrichmentModules(saasModule) ?? [] : []),
    [saasModule],
  );
  const userHasPinned = userPinned.length > 0;
  const selected = userHasPinned ? userPinned : systemDefaults;
  const selectedSet = useMemo(() => new Set(selected.map(String)), [selected]);

  const cap = getEnrichmentModulesCap(saasModule || null);
  const capLabel = Number.isFinite(cap) ? `${cap}` : null;

  // Lock body scroll + Esc-to-close while panel is open. Also reset
  // the filter input each time the panel re-opens so stale text from
  // a previous session doesn't hide everything on next open.
  useEffect(() => {
    if (!open) {
      setFilter('');
      return;
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Case-insensitive filter over module labels (and values, for boards
  // where the user might remember the id). Empty filter shows all.
  const filteredAvailable = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return available;
    return available.filter((m) =>
      m.label.toLowerCase().includes(q) || String(m.value).toLowerCase().includes(q)
    );
  }, [available, filter]);

  if (!saasModule || available.length === 0) return null;

  const saasName = saasModule.name || 'CRM';

  // Resolve labels for the current selection — boards on Monday come
  // back as { value: boardId, label: boardName }, so this turns IDs
  // into human-readable names in the trigger.
  const labelByValue = new Map(available.map((m) => [String(m.value), m.label] as const));
  const selectedLabels = selected.map((v) => labelByValue.get(String(v)) || String(v));
  const triggerText =
    selectedLabels.length === 0
      ? 'No module selected'
      : selectedLabels.length <= 3
        ? selectedLabels.join(', ')
        : `${selectedLabels.slice(0, 2).join(', ')} +${selectedLabels.length - 2}`;

  const persist = (next: string[]) => {
    const cleaned = next
      .map((v) => String(v))
      .filter((v, i, arr) => v && arr.indexOf(v) === i);
    const capped = Number.isFinite(cap) ? cleaned.slice(0, cap) : cleaned;
    updatePreferences({ inboxRecordEnrichmentModules: capped });
    idbCache.clearByKeyPattern('org_data', CONV_ENRICHMENT_KEY_PREFIX).catch(() => {});
  };

  const toggle = (value: string) => {
    const base = userHasPinned ? userPinned : systemDefaults;
    const isOn = base.map(String).includes(String(value));
    const next = isOn
      ? base.filter((v) => String(v) !== String(value))
      : [...base, value];
    persist(next);
  };

  const handleResync = async () => {
    if (resyncing) return;
    setResyncing(true);
    try {
      await resyncConversationEnrichment();
    } finally {
      setResyncing(false);
    }
  };

  return (
    <>
      <div className={cn('inline-flex items-center gap-1.5 max-w-full', className)}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border/60 bg-muted/30 hover:bg-muted text-xs text-foreground transition-colors min-w-0"
          title="Change which CRM modules are searched for each conversation"
        >
          <Settings2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-muted-foreground">Chat Name lookup:</span>
          <span className="truncate">{triggerText}</span>
          {!userHasPinned && selected.length > 0 && (
            <span className="shrink-0 text-[9px] uppercase tracking-wide text-muted-foreground/70 border border-border rounded px-1 py-0.5">
              default
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={handleResync}
          disabled={resyncing}
          className={cn(
            'shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-md border border-border/60 bg-muted/30 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors',
            resyncing && 'opacity-60 cursor-wait',
          )}
          title="Re Sync Chat Names"
          aria-label="Re Sync Chat Names"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', resyncing && 'animate-spin')} />
        </button>
      </div>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-50">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-[1px] animate-in fade-in-0"
              onClick={() => setOpen(false)}
            />
            {/* Right-side panel */}
            <div
              role="dialog"
              aria-label="Chat Name lookup modules"
              className="absolute right-0 top-0 h-full w-full sm:w-[400px] max-w-full flex flex-col bg-background border-l border-border shadow-xl animate-slide-in-right"
            >
              <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-border/60">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-foreground">Chat Name lookup</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {capLabel
                      ? `Pick up to ${capLabel} ${saasName} module${capLabel === '1' ? '' : 's'} to find the matching contact for each chat.`
                      : `Pick the ${saasName} boards to find the matching contact for each chat.`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="shrink-0 rounded-sm p-1 opacity-70 hover:opacity-100 transition-opacity"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4">
                {available.length > 6 && (
                  <div className="relative mb-3">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    <input
                      type="search"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      placeholder={`Search ${saasName} ${capLabel ? 'modules' : 'boards'}…`}
                      className="w-full h-8 pl-7 pr-2 rounded-md border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-primary/40"
                      autoFocus
                    />
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {filteredAvailable.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No matches.</p>
                  ) : filteredAvailable.map((m) => {
                    const isSelected = selectedSet.has(String(m.value));
                    const atCap = Number.isFinite(cap) && selected.length >= cap;
                    const disabled = !isSelected && atCap;
                    return (
                      <button
                        key={String(m.value)}
                        type="button"
                        onClick={() => !disabled && toggle(String(m.value))}
                        disabled={disabled}
                        aria-pressed={isSelected}
                        title={disabled ? `Limit of ${capLabel} reached — remove one to pick another.` : m.label}
                        className={cn(
                          'px-2.5 py-1 rounded-full text-xs border transition-colors',
                          isSelected
                            ? 'bg-primary/10 border-primary/40 text-foreground'
                            : 'bg-background border-border text-muted-foreground hover:text-foreground hover:border-foreground/40',
                          disabled && 'opacity-40 cursor-not-allowed',
                        )}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>

                {!userHasPinned && systemDefaults.length > 0 && (
                  <p className="mt-4 text-[11px] text-muted-foreground">
                    Showing defaults — pick a module to override.
                  </p>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
